/**
 * Ribbon phone helpers for FullWidthRibbon.
 * Pure functions mirror existing component behaviour so unit tests can cover
 * CMS gating + Pulse null payload without changing runtime rules.
 */

export type RibbonPhoneFieldCms = {
  isVisible?: boolean | null
  isMandatory?: boolean | null
} | null | undefined

export type RibbonPhoneConfig = {
  isVisible: boolean
  isMandatory: boolean
}

export type LeadGenPayload = Record<string, unknown> & {
  phoneNumber?: unknown
}

export type RibbonPhoneFormField = {
  value?: string
  required?: boolean
  valid?: boolean
}

/** Maps CMS phoneNumberField toggles — mandatory only when also visible. */
export function resolveRibbonPhoneConfig(
  phoneNumberField: RibbonPhoneFieldCms,
): RibbonPhoneConfig {
  return {
    isVisible: phoneNumberField?.isVisible === true,
    isMandatory:
      phoneNumberField?.isVisible === true &&
      phoneNumberField?.isMandatory === true,
  }
}

/**
 * Keeps form phone required/valid flags in sync with CMS settings
 * (same rules as FullWidthRibbon phoneConfig watcher).
 */
export function syncRibbonPhoneFormField(
  phoneNumber: RibbonPhoneFormField | null | undefined,
  config: RibbonPhoneConfig,
): void {
  if (!phoneNumber) return
  phoneNumber.required = config.isMandatory
  if (!config.isVisible || !config.isMandatory) {
    phoneNumber.valid = true
  } else if (!phoneNumber.value) {
    phoneNumber.valid = false
  }
}

/** Mandatory empty phone → block submit with this message; otherwise clear. */
export function getRibbonMandatoryPhoneError(
  isMandatory: boolean,
  phoneValid: boolean,
): string {
  if (isMandatory && !phoneValid) {
    return 'Please fill all mandatory fields to continue.'
  }
  return ''
}

/**
 * Pulse phone after sanitize (which omits empty phone):
 * - Hidden (isVisible false): leave omitted — same as pre–phone-field ribbon
 * - Visible + empty: set phoneNumber to null
 * - Visible + filled: keep Pulse phone object
 */
export function applyRibbonEmptyPhoneAsNull<T extends LeadGenPayload>(
  payload: T,
  isVisible: boolean,
): T {
  if (!isVisible) {
    return payload
  }
  if (!payload.phoneNumber) {
    payload.phoneNumber = null
  }
  return payload
}
